/**
 * PreviewCardTP — "How it looks in the app" preview for a Tapping
 * Point. Same two-layer composite the TappingPointsTableV2 uses on
 * hover (avatar man behind, the tapping_point_image_url overlay on
 * top) but with the avatar permanently visible, so authors can see
 * the live composite while editing.
 *
 * Layer order (non-default row):
 *   z 0  — Avatar man illustration (always shown here, unlike the
 *          list where it fades in on hover)
 *   z 1  — `tapping_point_image_url` — transparent PNG of the dot
 *          markers, composited on top
 *   z 2+ — "App preview" frosted pill bottom-left for context
 *
 * The default tapping point row IS the avatar — its
 * `tapping_point_image_url` is the full avatar image, not a
 * transparent overlay. So the underlayer is suppressed for the
 * default row and we just show the upload directly with the
 * "Default" coral pill in the top-right.
 */
import React from 'react';
import RailCard, { RailCardHead } from '../../redesign/edit/RailCard';

const AVATAR_BG = '/img/avatar_man_large.webp';

export default function PreviewCardTP({ state }) {
  const isDefault = !!state.tapping_point_is_default;
  const overlayUrl = state.tapping_point_image_url || '';
  const name = state.tapping_point_name || 'Untitled tapping point';

  return (
    <RailCard
      variant="preview"
      head={({ open, toggle }) => (
        <RailCardHead
          variant="preview"
          eyebrow="How it looks in the app"
          open={open}
          onToggle={toggle}
        />
      )}
    >
      {/* Stage — cream matte behind the composite tile. */}
      <div
        style={{
          padding: 16,
          background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EDE4 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 240,
            aspectRatio: '3 / 4',
            borderRadius: 14,
            overflow: 'hidden',
            position: 'relative',
            background: '#2C3E50', /* navy fallback while the avatar loads */
            boxShadow: '0 8px 20px #2C3E5025, 0 1px 0 #FFFFFF80 inset',
          }}
        >
          {/* Layer 0 — avatar illustration. Suppressed on the
              default row: the default tapping point IS the
              avatar (its `tapping_point_image_url` is the full
              avatar image, not a transparent overlay), so
              painting a second avatar underneath would double up. */}
          {!isDefault && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url('${AVATAR_BG}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#2C3E50',
                zIndex: 0,
              }}
            />
          )}

          {/* Layer 1 — the uploaded image. For non-default rows
              this is the transparent dot-marker PNG composited
              over the avatar; for the default row it's the
              avatar image itself, shown solo. */}
          {overlayUrl && (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url('${overlayUrl}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                backgroundRepeat: 'no-repeat',
                zIndex: 1,
              }}
            />
          )}

          {/* Empty-state hint when no overlay yet (still shows the
              avatar so the composite makes sense once a PNG is
              uploaded). */}
          {!isDefault && !overlayUrl && (
            <div
              className="font-condensed font-bold uppercase"
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 14,
                textAlign: 'center',
                color: '#FFFFFFCC',
                textShadow: '0 1px 3px #00000060',
                fontSize: 10.5,
                letterSpacing: '0.1em',
                zIndex: 2,
              }}
            >
              Upload the dot-marker PNG ↓
            </div>
          )}

          {/* "App preview" frosted pill bottom-left (matches the
              list's hover affordance). Suppressed on the default
              row since there's no composite to read. */}
          {!isDefault && (
            <span
              aria-hidden
              className="font-condensed font-bold uppercase"
              style={{
                position: 'absolute',
                left: 10,
                bottom: 10,
                padding: '3px 9px',
                borderRadius: 999,
                background: '#00000080',
                backdropFilter: 'blur(3px)',
                color: '#FFFFFF',
                fontSize: 10,
                letterSpacing: '0.08em',
                zIndex: 3,
              }}
            >
              App preview
            </span>
          )}

          {/* "Default" coral pill top-right when applicable. */}
          {isDefault && (
            <span
              aria-hidden
              className="font-condensed font-bold uppercase"
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                padding: '3px 9px',
                borderRadius: 999,
                background: '#E58A6A',
                color: '#FFFFFF',
                fontSize: 10,
                letterSpacing: '0.08em',
                boxShadow: '0 2px 6px #00000028',
                zIndex: 3,
              }}
            >
              Default
            </span>
          )}
        </div>
      </div>

      {/* Foot — show the current name as a small label below the
          composite. No universal link to display for tapping
          points, so we use the foot to confirm what's being
          previewed. */}
      <div
        style={{
          padding: '10px 14px',
          borderTop: '1px solid #E8E0CF',
          background: '#FAF4EA',
          fontSize: 11.5,
          color: '#2C3E50',
          fontFamily: 'Asap, sans-serif',
          fontWeight: 500,
          textAlign: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={name}
      >
        {name}
      </div>
    </RailCard>
  );
}
